import { useMemo } from "react";
import { FixedMixCreate, FixedMixDefaultsRead } from "store/api/generatedApi";
import { editableFixedMixes } from "./fixedMixEdits";
import { fixedMixDraftsMatch, groupFixedMixes } from "./fixedMixRows";
import { useDefaultDraft } from "./useDefaultDraft";

interface FixedMixDraft {
  mixes: FixedMixCreate[];
  mixGroups: string[][];
}

const createDraft = (source: FixedMixDefaultsRead): FixedMixDraft => ({
  mixes: editableFixedMixes(source.effective.mixes),
  mixGroups: source.mix_groups,
});
const normaliseDraft = (
  source: FixedMixDefaultsRead,
  proposed: FixedMixDraft
) => (fixedMixDraftsMatch(createDraft(source), proposed) ? null : proposed);
const sourcesMatch = (
  first: FixedMixDefaultsRead,
  second: FixedMixDefaultsRead
) => JSON.stringify(first.context) === JSON.stringify(second.context);

export const useFixedMixDraft = (defaults: FixedMixDefaultsRead) => {
  const lifecycle = useDefaultDraft({
    defaultSource: defaults,
    createDraft,
    normaliseDraft,
    sourcesMatch,
  });
  const { source, draft } = lifecycle;
  const sourceMixes = useMemo(
    () => editableFixedMixes(source.effective.mixes),
    [source.effective.mixes]
  );
  const groups = useMemo(() => groupFixedMixes(draft), [draft]);
  const request = useMemo(
    () => ({
      context: source.context,
      mixes: draft.mixes,
      mix_groups: groups.map((group) => group.gradeNames),
    }),
    [source.context, draft.mixes, groups]
  );
  const savedPreview = source.effective_preview;
  const usesSavedPreview =
    fixedMixDraftsMatch(draft, {
      mixes: sourceMixes,
      mixGroups: source.mix_groups,
    }) ||
    (savedPreview !== null &&
      fixedMixDraftsMatch(draft, {
        mixes: savedPreview.mixes,
        mixGroups: source.mix_groups,
      }));
  return {
    ...lifecycle,
    sourceMixes,
    groups,
    request,
    previewRequest: usesSavedPreview ? null : request,
  };
};
